import type { ThreeEvent } from '@react-three/fiber'
import { useAppSelector } from 'common/hooks/redux'
import { DescriptionLabel } from 'features/Procedure/procedure-model/description-label/description-label'
import { ElementIdLabel } from 'features/Procedure/procedure-model/element-id-label'
import type { DrawingElementDescription } from 'features/Procedure/state/procedure.type'
import { formatDrawingNoteDisplay } from 'features/Procedure/utils/drawingDescription.utils'
import { getProcedureOpacity } from 'features/Procedure/utils/utils'
import React, { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { Vector3 } from 'three'
import { materialCache } from './material-cache'
import {
  calculateArrowPositionAndDirection,
  calculateArrowPositionsAndDirections,
  createCurve,
  createLineGeometry,
  createPickGeometry,
  getCacheKey,
} from './utils'

type Props = {
  startPoint: Vector3
  endPoint: Vector3
  color: string
  lineWidth: number
  bodyMesh?: THREE.Object3D
  cameraPosition?: THREE.Vector3
  showMeasurement?: boolean
  showDirection?: boolean
  isSelected?: boolean
  isFromSelectedProcedure?: boolean
  showElementIds?: boolean
  onClick?: (event: ThreeEvent<PointerEvent>) => void
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void
  onPointerEnter?: (event: ThreeEvent<PointerEvent>) => void
  onPointerOver?: (event: ThreeEvent<PointerEvent>) => void
  description?: DrawingElementDescription
  isPreview?: boolean
  useDepthTest?: boolean
  inactiveOpacity?: number
  readOnly?: boolean
  interactive?: boolean
}

export const StraightLine = React.memo(
  ({
    startPoint,
    endPoint,
    color,
    lineWidth,
    bodyMesh,
    cameraPosition,
    showMeasurement = true,
    showDirection = true,
    isSelected = false,
    isFromSelectedProcedure = false,
    showElementIds: showElementIdsOverride,
    onClick,
    onPointerDown,
    onPointerEnter,
    onPointerOver,
    description,
    isPreview = false,
    useDepthTest = false,
    inactiveOpacity = 0,
    readOnly = false,
    interactive = true,
  }: Props) => {
    const descriptionText = useMemo(() => formatDrawingNoteDisplay(description), [description])
    const lineRef = useRef<THREE.Mesh>(null)
    const pickRef = useRef<THREE.Mesh>(null)
    const arrowMaterialRef = useRef<THREE.MeshStandardMaterial>(null)
    const chevronStartMaterialRef = useRef<THREE.MeshStandardMaterial>(null)
    const chevronEndMaterialRef = useRef<THREE.MeshStandardMaterial>(null)
    const curveRef = useRef<THREE.Curve<Vector3> | null>(null)
    const lastParamsRef = useRef<string>('')
    const storeShowElementIds = useAppSelector((state) => state.procedureReducer.showElementIds)
    const isPrinting = useAppSelector((state) => state.procedureReducer.isPrinting)
    const [, setCurveVersion] = React.useState(0)
    const showElementIds = showElementIdsOverride ?? storeShowElementIds

    React.useEffect(() => {
      if (lineRef.current) {
        const cacheKey = getCacheKey(startPoint, endPoint, cameraPosition, !!bodyMesh)

        let curve: THREE.Curve<Vector3>

        if (curveRef.current && lastParamsRef.current === cacheKey) {
          curve = curveRef.current
        } else {
          curve = createCurve(startPoint, endPoint, bodyMesh, cameraPosition)
          curveRef.current = curve
          lastParamsRef.current = cacheKey
          setCurveVersion((v) => v + 1)
        }

        if (lineRef.current.geometry) {
          lineRef.current.geometry.dispose()
        }

        const geometry = createLineGeometry(curve, lineWidth, showDirection, showMeasurement)
        const material = materialCache.getLineMaterial(
          color,
          isSelected,
          isFromSelectedProcedure,
          isPrinting,
          isPreview,
          useDepthTest,
          inactiveOpacity,
        )

        lineRef.current.geometry = geometry
        lineRef.current.material = material

        if (!readOnly && interactive && pickRef.current) {
          if (pickRef.current.geometry) {
            pickRef.current.geometry.dispose()
          }

          const pickGeometry = createPickGeometry(curve, lineWidth, showDirection, showMeasurement)
          const pickMaterial = materialCache.getPickMaterial()
          pickRef.current.geometry = pickGeometry
          pickRef.current.material = pickMaterial
        }
      }

      return () => {
        if (lineRef.current?.geometry) {
          lineRef.current.geometry.dispose()
        }
        if (pickRef.current?.geometry) {
          pickRef.current.geometry.dispose()
        }
      }
    }, [
      startPoint.x,
      startPoint.y,
      startPoint.z,
      endPoint.x,
      endPoint.y,
      endPoint.z,
      color,
      lineWidth,
      bodyMesh,
      cameraPosition?.x,
      cameraPosition?.y,
      cameraPosition?.z,
      showDirection,
      showMeasurement,
      isSelected,
      isFromSelectedProcedure,
      isPrinting,
      isPreview,
      useDepthTest,
      inactiveOpacity,
      readOnly,
      interactive,
    ])

    useEffect(() => {
      if (arrowMaterialRef.current) {
        arrowMaterialRef.current.transparent = !isFromSelectedProcedure && !isPrinting && !isPreview
        arrowMaterialRef.current.opacity = getProcedureOpacity(
          isFromSelectedProcedure,
          isPrinting,
          isPreview,
          inactiveOpacity,
        )
        arrowMaterialRef.current.needsUpdate = true
      }
      if (chevronStartMaterialRef.current) {
        chevronStartMaterialRef.current.transparent =
          !isFromSelectedProcedure && !isPrinting && !isPreview
        chevronStartMaterialRef.current.opacity = getProcedureOpacity(
          isFromSelectedProcedure,
          isPrinting,
          isPreview,
          inactiveOpacity,
        )
        chevronStartMaterialRef.current.needsUpdate = true
      }
      if (chevronEndMaterialRef.current) {
        chevronEndMaterialRef.current.transparent =
          !isFromSelectedProcedure && !isPrinting && !isPreview
        chevronEndMaterialRef.current.opacity = getProcedureOpacity(
          isFromSelectedProcedure,
          isPrinting,
          isPreview,
          inactiveOpacity,
        )
        chevronEndMaterialRef.current.needsUpdate = true
      }
    }, [isFromSelectedProcedure, isPrinting, isPreview, inactiveOpacity])

    useEffect(() => {
      if (arrowMaterialRef.current) {
        arrowMaterialRef.current.depthTest = useDepthTest
        arrowMaterialRef.current.needsUpdate = true
      }
      if (chevronStartMaterialRef.current) {
        chevronStartMaterialRef.current.depthTest = useDepthTest
        chevronStartMaterialRef.current.needsUpdate = true
      }
      if (chevronEndMaterialRef.current) {
        chevronEndMaterialRef.current.depthTest = useDepthTest
        chevronEndMaterialRef.current.needsUpdate = true
      }
    }, [useDepthTest])

    const midPoint = curveRef.current
      ? curveRef.current.getPointAt(0.5)
      : new Vector3().lerpVectors(startPoint, endPoint, 0.5)

    const arrowData =
      showDirection && curveRef.current
        ? calculateArrowPositionAndDirection(curveRef.current, lineWidth)
        : null

    const measurementChevronsData =
      showMeasurement && curveRef.current
        ? calculateArrowPositionsAndDirections(curveRef.current, lineWidth)
        : null

    const handleClick = (event: ThreeEvent<PointerEvent>) => {
      if (onClick) {
        event.stopPropagation()
        onClick(event)
      }
    }

    const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
      onPointerDown?.(event)
    }

    const handlePointerEnter = (event: ThreeEvent<PointerEvent>) => {
      onPointerEnter?.(event)
    }

    const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
      onPointerOver?.(event)
    }

    const canInteract = interactive && !readOnly
    const pointerHandlers = canInteract
      ? {
          onClick: handleClick,
          onPointerDown: handlePointerDown,
          onPointerEnter: handlePointerEnter,
          onPointerOver: handlePointerOver,
        }
      : {}

    return (
      <>
        {}
        {canInteract && <mesh ref={pickRef} {...pointerHandlers} />}
        <mesh ref={lineRef} {...pointerHandlers} />

        {showDirection &&
          arrowData &&
          (() => {
            const { position, direction, arrowLength } = arrowData
            const arrowRadius = (lineWidth / 100) * 3

            // Declarative <coneGeometry> so R3F owns the buffer lifecycle and
            // disposes it on unmount or args change. The previous inline
            // `new THREE.ConeGeometry(...)` allocated a fresh GPU buffer every
            // render and never disposed it, leaking on every Redux update.
            // We shift the mesh by -direction*arrowLength/2 to compensate for the
            // dropped `geometry.translate(0, -arrowLength/2, 0)` so the cone tip
            // still lands at `position`.
            const tipOffset = direction.clone().multiplyScalar(-arrowLength / 2)
            const meshPosition = position.clone().add(tipOffset)

            const quaternion = new THREE.Quaternion()
            quaternion.setFromUnitVectors(new Vector3(0, 1, 0), direction)

            const arrowColor = new THREE.Color(color).multiplyScalar(0.8)
            const arrowEmissiveIntensity = isSelected ? 0.3 : 0.1

            return (
              <mesh position={meshPosition} quaternion={quaternion} {...pointerHandlers}>
                <coneGeometry args={[arrowRadius, arrowLength, 8]} />
                <meshStandardMaterial
                  ref={arrowMaterialRef}
                  color={arrowColor}
                  emissive={arrowColor}
                  emissiveIntensity={arrowEmissiveIntensity}
                  roughness={0.4}
                  metalness={0.1}
                  transparent={!isFromSelectedProcedure && !isPrinting && !isPreview}
                  opacity={getProcedureOpacity(
                    isFromSelectedProcedure,
                    isPrinting,
                    isPreview,
                    inactiveOpacity,
                  )}
                  depthTest={useDepthTest}
                  depthWrite={false}
                  polygonOffset={useDepthTest}
                  polygonOffsetFactor={useDepthTest ? -12 : 0}
                  polygonOffsetUnits={useDepthTest ? -12 : 0}
                />
              </mesh>
            )
          })()}

        {showMeasurement &&
          measurementChevronsData &&
          (() => {
            const { start, end } = measurementChevronsData
            const chevronRadius = (lineWidth / 100) * 3
            const arrowColor = new THREE.Color(color).multiplyScalar(0.8)
            const arrowEmissiveIntensity = isSelected ? 0.3 : 0.1

            const createChevron = (
              arrowData: { position: Vector3; direction: Vector3; arrowLength: number },
              key: string,
              materialRef: React.RefObject<THREE.MeshStandardMaterial>,
            ) => {
              const { position, direction, arrowLength } = arrowData
              // Same lifecycle fix as the directional arrow: declarative geometry
              // + mesh-position offset replaces the leaky inline ConeGeometry.
              const tipOffset = direction.clone().multiplyScalar(-arrowLength / 2)
              const meshPosition = position.clone().add(tipOffset)

              const quaternion = new THREE.Quaternion()
              quaternion.setFromUnitVectors(new Vector3(0, 1, 0), direction)

              return (
                <mesh key={key} position={meshPosition} quaternion={quaternion} {...pointerHandlers}>
                  <coneGeometry args={[chevronRadius, arrowLength, 8]} />
                  <meshStandardMaterial
                    ref={materialRef}
                    color={arrowColor}
                    emissive={arrowColor}
                    emissiveIntensity={arrowEmissiveIntensity}
                    roughness={0.4}
                    metalness={0.1}
                    transparent={!isFromSelectedProcedure && !isPrinting && !isPreview}
                    opacity={getProcedureOpacity(
                      isFromSelectedProcedure,
                      isPrinting,
                      isPreview,
                      inactiveOpacity,
                    )}
                    depthTest={useDepthTest}
                    depthWrite={false}
                    polygonOffset={useDepthTest}
                    polygonOffsetFactor={useDepthTest ? -12 : 0}
                    polygonOffsetUnits={useDepthTest ? -12 : 0}
                  />
                </mesh>
              )
            }

            return (
              <>
                {createChevron(start, 'start', chevronStartMaterialRef)}
                {createChevron(end, 'end', chevronEndMaterialRef)}
              </>
            )
          })()}

        {showMeasurement && descriptionText && isFromSelectedProcedure && (
          <DescriptionLabel
            description={descriptionText}
            position={midPoint}
            color={color}
            useDepthTest={useDepthTest}
          />
        )}

        {showElementIds && isFromSelectedProcedure && !showMeasurement && descriptionText && (
          <ElementIdLabel
            text={descriptionText}
            position={midPoint}
            color={color}
            useDepthTest={false}
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerEnter={onPointerEnter}
            onPointerOver={onPointerOver}
            offset={{
              x: Math.abs(midPoint.x) > Math.abs(midPoint.z) ? (midPoint.x > 0 ? 1 : -1) : 0,
              y: 0.5,
              z: Math.abs(midPoint.z) >= Math.abs(midPoint.x) ? (midPoint.z >= 0 ? 1 : -1) : 0,
            }}
          />
        )}
      </>
    )
  },
)
